/* Presentation mode.

   A slide is not a page. It is read from six metres away, in one breath, and
   the eye has to land on the one thing that matters before it reads anything
   else. So the deck runs its own scale, its own spacing and its own surfaces —
   see styles/deck.css — and its own rule about what a press does.

   One press reveals the next held-back step, and only when there is none does
   it move to the next slide. Stepping back past the first step enters the
   previous slide **fully revealed**, because going back is how somebody returns
   to something they have already shown.

   OWNED BY ITEM 8 OF docs/plan/00-prestavba-2026-08.md. */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Blocks } from "../../blocks";
import { fitSlide } from "../../lib/fit";
import { useDoc } from "../../lib/hooks";
import { useI18n } from "../../lib/i18n";
import { useFeature, usePortal } from "../../lib/portal";
import { LoadFailed } from "../NotFound";
import { RevealContext, maxStep } from "./reveal";

/* The bar at the foot of the screen is 56px and the slide is laid out above it. */
const BAR = 56;

export function Deck() {
  const { id = "", n = "1" } = useParams();
  const { t, ui, href } = useI18n();
  const navigate = useNavigate();
  const on = useFeature("deck");
  /* "Not answered for yet" means off when the question is whether to *draw* a
     feature — guessing wrong there can spend money. It must not mean off when
     the question is whether to *leave the page*: the settings arrive a moment
     after the first render, and redirecting on that moment throws the reader
     out of the deck every time they open it. */
  const { settings } = usePortal();
  const { data: doc, error, loading } = useDoc(id);

  const [step, setStep] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  /* Set when the reader steps backwards into a slide, so it opens finished
     rather than making them press through it again. */
  const enterAtEnd = useRef(false);

  const parts = doc?.parts ?? [];
  const count = parts.length;
  const index = Math.max(1, Math.min(count || 1, parseInt(n, 10) || 1));
  const slide = parts[index - 1];
  const held = slide ? maxStep(slide.blocks) : 0;

  /* The deck takes over the whole screen: no header, no footer, no scrolling. */
  useEffect(() => {
    document.body.classList.add("deck");
    return () => document.body.classList.remove("deck");
  }, []);

  useEffect(() => {
    setStep(enterAtEnd.current ? maxStep(parts[index - 1]?.blocks ?? []) : 0);
    enterAtEnd.current = false;
  }, [index, doc]);   // eslint-disable-line react-hooks/exhaustive-deps

  const go = useCallback((to: number, atEnd = false) => {
    enterAtEnd.current = atEnd;
    navigate(href(`/deck/${id}/${to}`), { replace: false });
  }, [href, id, navigate]);

  const move = useCallback((dir: 1 | -1) => {
    if (dir > 0) {
      if (step < held) return setStep(step + 1);
      if (index < count) go(index + 1);
    } else {
      if (step > 0) return setStep(step - 1);
      if (index > 1) go(index - 1, true);
    }
  }, [count, go, held, index, step]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") {
        move(1); e.preventDefault();
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        move(-1); e.preventDefault();
      } else if (e.key === "Escape") {
        navigate(href(`/p/${id}`));
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [href, id, move, navigate]);

  /* Measured after layout, not after paint: the reader must never see the slide
     at the wrong size first. Re-run on every slide and every reveal, because a
     revealed step keeps its space but the fonts may have only just landed. */
  useLayoutEffect(() => {
    if (!box.current) return;
    const fit = () => box.current && fitSlide(box.current, window.innerHeight - BAR - 40);
    fit();
    window.addEventListener("resize", fit);
    if (document.fonts?.ready) void document.fonts.ready.then(fit);
    return () => window.removeEventListener("resize", fit);
    /* `settings` is in here because the slide is not rendered until it arrives:
       without it the first run finds no element and the second never happens,
       and the slide sits at 1.0 on a projector that had room for 1.2. */
  }, [index, doc, settings]);

  if (loading || settings === null) return null;
  if (error || !doc) return <LoadFailed error={error} />;
  /* Presentation mode is switchable on /admin. Switched off, the theory is
     still a page — the reading route is never the thing that disappears. */
  if (!on || !count) return <Navigate to={href(`/p/${id}`)} replace />;
  if (!slide) return <Navigate to={href(`/deck/${id}/1`)} replace />;

  return (
    <RevealContext.Provider value={step}>
      <div className={"slide" + (slide.variant ? ` v-${slide.variant}` : "")}>
        <div className="fit" ref={box}>
          <div className="slide-in">
            {slide.eyebrow ? <div className="eyebrow">{t(slide.eyebrow)}</div> : null}
            {slide.title ? <h2>{t(slide.title)}</h2> : null}
            {slide.lead ? <div className="lead">{t(slide.lead)}</div> : null}
            <div className="body"><Blocks blocks={slide.blocks} /></div>
          </div>
        </div>
      </div>

      <div className="deckbar">
        <button type="button" onClick={() => move(-1)} aria-label={ui("deck.back")}>←</button>
        <button type="button" onClick={() => move(1)} aria-label={ui("deck.forward")}>→</button>
        <span>{index} / {count}</span>
        {/* How many reveals the slide still holds back. Without it a lecturer
            cannot tell a finished slide from one with an answer still to come. */}
        <span className="dots">
          {held ? Array.from({ length: held + 1 }, (_, i) => (
            <i key={i} className={i <= step ? "on" : undefined} />
          )) : null}
        </span>
        <span className="prog"><i style={{ width: `${(100 * index) / count}%` }} /></span>
        <button type="button" className="deck-out" onClick={() => navigate(href(`/p/${id}`))}>
          {ui("deck.leave")}
        </button>
        <span>Esc</span>
      </div>
    </RevealContext.Provider>
  );
}
